import React from 'react';
import { useBranding } from '../../contexts/BrandingContext';

/**
 * PlatformWordmark — Tenant-aware wordmark component
 *
 * Renders platform-specific wordmarks:
 * - ∑n-route: Sigma symbol (gold) + "n" + hyphen (teal) + "route"
 * - EazyCourseEngine: Three-part colored wordmark
 * - GuruSkool: Single-color wordmark
 *
 * @param {Object} props
 * @param {boolean} props.showTagline - Show tagline below wordmark (default: false)
 * @param {string} props.size - Size variant: 'sm' | 'md' | 'lg' | 'xl' (default: 'md')
 * @param {string} props.className - Additional CSS classes for the wrapper
 * @param {boolean} props.forceLightText - Force light (white) text for dark backgrounds (default: auto-detect)
 */
const ECEWordmark = ({ showTagline = false, size = 'md', className = '', forceLightText = false }) => {
  const branding = useBranding();

  // Size variants based on ece-final-v4.html spec
  const sizeClasses = {
    sm: 'text-sm',      // 14px - compact spaces
    md: 'text-[15px]',  // 15px - topbar standard (from spec)
    lg: 'text-lg',      // 18px - desktop nav
    xl: 'text-2xl',     // 24px - landing hero
  };

  const taglineSizes = {
    sm: 'text-[11px]',   // Bumped from 8px for readability
    md: 'text-[12px]',   // Bumped from 9px
    lg: 'text-[13px]',   // Bumped from 10px
    xl: 'text-[14px]',   // Bumped from 12px
  };

  // Sigma size slightly larger for visual balance
  const sigmaSizes = {
    sm: 'text-base',
    md: 'text-[17px]',  // Slightly larger than 15px base
    lg: 'text-xl',
    xl: 'text-3xl',
  };

  // Detect platform
  const isEnroute = branding.platform_name === '∑n-route' || branding.platform_plain_name === 'nroute';
  const isEazyCourse = branding.platform_name === 'EazyCourseEngine';

  // For Enroute: use white text on dark backgrounds, navy on light
  const isDark = forceLightText || document.documentElement.classList.contains('dark');
  const enrouteTextColor = isEnroute ? (isDark ? '#FFFFFF' : '#0B2540') : branding.primary_color;

  return (
    <div className={`flex flex-col gap-[1px] ${className}`}>
      {/* Platform name wordmark */}
      <div
        className={`font-syne font-bold tracking-[-0.025em] leading-none whitespace-nowrap ${sizeClasses[size]}`}
      >
        {isEnroute ? (
          // ∑n-route wordmark
          <>
            <span className={sigmaSizes[size]} style={{ color: branding.sigma_color || branding.secondary_color }}>∑</span>
            <span style={{ color: enrouteTextColor }}>n</span>
            <span style={{ color: branding.hyphen_color || branding.accent_color, opacity: 0.6 }}>-</span>
            <span style={{ color: enrouteTextColor }}>route</span>
          </>
        ) : isEazyCourse ? (
          // EazyCourseEngine wordmark
          <>
            <span style={{ color: branding.primary_color }}>Eazy</span>
            <span style={{ color: branding.secondary_color }}>Course</span>
            <span style={{ color: branding.primary_color }}>Engine</span>
          </>
        ) : (
          // Default single-color wordmark (GuruSkool, etc.)
          <span style={{ color: branding.primary_color }}>{branding.platform_name}</span>
        )}
      </div>

      {/* Tagline - elevated styling for visibility */}
      {showTagline && (
        <div
          style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            fontStyle: "italic",
            fontSize: taglineSizes[size].replace('text-[', '').replace(']', ''),
            letterSpacing: "0.02em",
            color: isDark ? 'rgba(241, 245, 249, 0.85)' : 'rgba(11, 37, 64, 0.75)', // NR.cream / navy with opacity
            fontWeight: 400,
            whiteSpace: 'nowrap'
          }}
        >
          {branding.tagline}
        </div>
      )}
    </div>
  );
};

export default ECEWordmark;
